import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { BaseIndexPageComponent } from 'src/app/components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from 'src/app/components/pages/base-index-page/base-index-page.config';
import { DataTableColumnText } from 'src/app/components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { SelectOption } from 'src/app/components/form/select-field/select-field.config';
import ApiOptionsConfig from 'src/app/services/api/api-options.config';
import { IntroducersService } from 'src/app/services/api/introducers.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AccessControlService } from 'src/app/services/access-control.service';

@Component({
  selector: 'app-introducer-clients-list-page',
  imports: [CommonModule, ReactiveFormsModule, BaseIndexPageComponent, InputFieldComponent, SelectFieldComponent],
  templateUrl: './introducer-clients-list-page.component.html',
  styleUrl: './introducer-clients-list-page.component.scss',
})
export class IntroducerClientsListPageComponent implements OnInit, OnDestroy {
  @ViewChild(BaseIndexPageComponent, { static: true }) baseIndexPage?: BaseIndexPageComponent;

  baseIndexPageConfig: BaseIndexPageConfig;

  introducerOptions: SelectOption[] = [];
  introducerControl = new FormControl<any>(null);
  isIntroducerSelectLocked = false;
  startingDateControl = new FormControl<string>('');

  private introducers: any[] = [];
  private subscriptions = new Subscription();

  constructor(
    protected introducersService: IntroducersService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
  ) {
    this.baseIndexPageConfig = {
      pageTitle: "Introducers clients",
      cardTitle: "Introducers clients",
      dataTable: {
        data: [],
        pagination: null,
        searchModel: {
          clientName: null,
          introducerPercentMin: null,
          introducerPercentMax: null,
          revenuePeriodStartDate: null,
          revenuePeriodEndDate: null,
          totalTurnoverMin: null,
          totalTurnoverMax: null,
        },
        columns: [
          new DataTableColumnText({
            attribute: 'clientName',
            header: 'Client Name',
            filter: 'text',
            value: (item: any) => item.clientName,
          }),
          new DataTableColumnText({
            attribute: 'introducerPercent',
            header: '% Introducer',
            filter: 'number-range',
            filterMinAttribute: 'introducerPercentMin',
            filterMaxAttribute: 'introducerPercentMax',
            value: (item: any) =>
              item.introducerPercent !== null && item.introducerPercent !== undefined
                ? `${item.introducerPercent} %`
                : '',
          }),
          new DataTableColumnText({
            attribute: 'revenuePeriodStartDate',
            header: 'Starting Date',
            filter: 'date',
            value: (item: any) => this.formatDate(item.revenuePeriodStartDate),
          }),
          new DataTableColumnText({
            attribute: 'revenuePeriodEndDate',
            header: 'Ending Date',
            filter: 'date',
            value: (item: any) => this.formatDate(item.revenuePeriodEndDate),
          }),
          new DataTableColumnText({
            attribute: 'totalTurnover',
            header: 'Total Turnover',
            filter: 'number-range',
            filterMinAttribute: 'totalTurnoverMin',
            filterMaxAttribute: 'totalTurnoverMax',
            value: (item: any) => this.formatCurrency(item.totalTurnover),
          }),
        ],
      },
      apiListService: (options: ApiOptionsConfig) => this.introducersService.listIntroducerClients(options),
      responseListModelName: 'clients',
    };
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        this.isIntroducerSelectLocked = !this.accessControlService.hasFeatureActionAccess(
          profileCode, 'INTRODUCER_SELECT_FILTER', 'modify'
        );
        if (this.isIntroducerSelectLocked) {
          this.introducerControl.disable({ emitEvent: false });
        } else {
          this.introducerControl.enable({ emitEvent: false });
        }
      })
    );
    this.loadIntroducers();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onIntroducerChange(introducerId: any): void {
    this.introducerControl.setValue(this.toId(introducerId), { emitEvent: false });
    this.applySelectedIntroducer();
    this.refresh();
  }

  private loadIntroducers(): void {
    this.introducersService
      .list()
      .subscribe((response: any) => {
        this.introducers = response?.data?.introducers ?? [];
        this.introducerOptions = this.introducers.map((introducer: any) => ({
          key: introducer.introducerId,
          value: introducer.name,
        }));

        if (!this.introducers.length) {
          return;
        }

        this.introducerControl.setValue(this.introducers[0].introducerId, { emitEvent: false });
        this.applySelectedIntroducer();
        this.refresh();
      });
  }

  private applySelectedIntroducer(): void {
    const introducer = this.selectedIntroducer();
    this.startingDateControl.setValue(
      introducer?.contractStartTime ? this.formatDate(introducer.contractStartTime) : ''
    );
  }

  private selectedIntroducer(): any {
    return this.introducers.find(
      (introducer: any) => String(introducer.introducerId) === String(this.introducerControl.value)
    );
  }

  private refresh(): void {
    this.baseIndexPage?.reload({ introducerId: this.introducerControl.value });
  }

  private toId(value: any): any {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  }

  private formatDate(value: any): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }

  private formatCurrency(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const amount = Number(value);
    if (Number.isNaN(amount)) {
      return '';
    }
    return `€ ${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
}
